import {
  Box,
  Drawer,
  DrawerContent,
  Flex,
  Heading,
  MenuItem,
} from "@chakra-ui/react";
import NextLink from "next/link";
import React, { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/router";
import Layout from "components/Layout";
import HeadTitle from "components/Shared/HeadTitle";
import DropDownExportMenu from "components/Products/DropDownExportMenu";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import {
  useClearRouteParamsOnBrandChange,
  useGetRouteParam,
} from "utils/routeParamHooks";
import { useCurrentBrandId } from "utils/currentUserHooks";
import ProductsTable, { RowTypes } from "components/Products/ProductsTable";
import ProductLinePanel from "components/Products/ProductLinePanel";
import ProductLineCreate from "components/Products/ProductLinePanel/Create";
import type { ProductLineInputs } from "components/Products/ProductLinePanel/Form";
import SkuPanel from "components/Products/SkuPanel";
import SkuPanelCreate from "components/Products/SkuPanel/Create";
import type { SkuInputs } from "components/Products/SkuPanel/utils";
import { productLinesSkusTableQuery } from "components/Products/queries";
import { CONTENT_FULL_HEIGHT_PROPS } from "components/Layout/constants";
import DropDownMenu from "components/Shared/DropDownMenu";
import {
  PRODUCTS_IMPORT,
  PRODUCTS_INDEX,
  selectedProductLine,
  selectedSku,
} from "utils/links";
import ScrollContainer from "components/Shared/ChakraReactTable/ScrollContainer";
import CreateButton from "components/Shared/CreateButton";
import RefreshButton from "components/Shared/RefreshButton";
import { asyncVoid } from "utils/asyncVoidHandler";
import { assertPresence } from "utils/assertions";
import ArchivedStatusButtonGroup, {
  ArchivedStatus,
} from "components/Shared/ArchivedStatusButtonGroup";
import {
  getArchivedProductLineFilter,
  getArchivedSkuFilter,
} from "components/Products/utils";
import FilterBarWithSearch, {
  FreestandingFilterBarContainer,
  FreestandingFilterWell,
} from "components/Shared/FilterEditor/FilterBar/FilterBarWithSearch";
import { useGraphileFiltersUrlQuery } from "components/Shared/FilterEditor/urlQuerySerializers";
import { ProductLineFilter, SkuFilter } from "__generated__/gql/graphql";
import filterDefinitions from "components/Products/filterDefinitions";
import { useOptionalCombineFilters } from "utils/useCombineFilters";

type CreateNewProductLine = [
  typename: "ProductLine",
  initialData?: ProductLineInputs,
];

type CreateNewSku = [typename: "Sku", initialData?: SkuInputs];

export type CreateNew = CreateNewProductLine | CreateNewSku;

export default function ProductsIndex() {
  const productLineId = useGetRouteParam("productLineId");
  const skuId = useGetRouteParam("skuId");
  useClearRouteParamsOnBrandChange(["productLineId", "skuId"]);
  const { push } = useRouter();
  const [createNew, setCreateNewState] = useState<CreateNew | null>(null);

  const { filter, filters, isTableReady, handleFiltersChange } =
    useGraphileFiltersUrlQuery<SkuFilter>(filterDefinitions, {
      externalQueryKeys: ["productLineId", "skuId"],
    });

  const brandId = useCurrentBrandId();

  const [archivedStatus, setArchivedStatus] = useState<ArchivedStatus>(
    ArchivedStatus.Active,
  );

  const skuArchivedFilter = useMemo(
    () => getArchivedSkuFilter(archivedStatus),
    [archivedStatus],
  );

  const productLineArchivedFilter = useMemo(
    () => getArchivedProductLineFilter(archivedStatus),
    [archivedStatus],
  );

  const productLineSkuFilter = useMemo<ProductLineFilter | undefined>(
    () => (filter ? { skus: { some: filter } } : undefined),
    [filter],
  );

  const skuFilter = useOptionalCombineFilters(skuArchivedFilter, filter);
  const productLineFilter = useOptionalCombineFilters(
    productLineArchivedFilter,
    productLineSkuFilter,
  );

  const { data, loading, refetch } = useBrandQuery(productLinesSkusTableQuery, {
    skip: !brandId || !isTableReady,
    variables: brandId ? { brandId, productLineFilter, skuFilter } : undefined,
    fetchPolicy: "cache-and-network",
  });

  const isLoading = loading || typeof window === "undefined";

  const numProductLines = data?.productLinesList?.length;

  const selectedRow = useMemo((): [typename: RowTypes, id: string] | null => {
    if (productLineId) return ["ProductLine", productLineId];
    if (skuId) return ["Sku", skuId];
    return null;
  }, [productLineId, skuId]);

  const handleRowSelected = useCallback(
    (typename: RowTypes, id: string) => {
      setCreateNewState(null);
      asyncVoid(
        push(
          typename === "ProductLine"
            ? selectedProductLine(id)
            : selectedSku(id),
          undefined,
          { shallow: true },
        ),
      );
    },
    [push],
  );

  const clearLocationQuery = useCallback(() => {
    asyncVoid(
      push({ pathname: PRODUCTS_INDEX, query: {} }, undefined, {
        shallow: true,
      }),
    );
  }, [push]);

  const handleClose = useCallback(() => {
    setCreateNewState(null);
    clearLocationQuery();
  }, [clearLocationQuery]);

  const handleCreateNew = (newCreateNew: CreateNew) => {
    setCreateNewState(newCreateNew);
    clearLocationQuery();
  };

  const handleArchiveSuccess = () => {
    if (archivedStatus !== ArchivedStatus.All) {
      handleClose();
    }
  };

  return (
    <ScrollContainer
      isNoBackground
      pb={10}
      display="flex"
      flexFlow="column"
      height="100%"
      width="100%"
    >
      <HeadTitle title="Product Information" />
      <Box
        marginBottom={2}
        px={{ base: 4, md: 12 }}
        pt={{ base: 4, md: 8 }}
        position="sticky"
        left={0}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={4}
          mb={4}
        >
          <Heading as="h1" variant="section-header">
            Product Information
          </Heading>

          <Flex
            justifyContent="flex-end"
            alignItems="center"
            flexFlow="row wrap"
            pl={2}
            gap={2}
            ml="auto"
          >
            <RefreshButton
              onClick={() => asyncVoid(refetch())}
              isLoading={isLoading}
            />

            <ArchivedStatusButtonGroup
              value={archivedStatus}
              onChange={setArchivedStatus}
            />

            <DropDownExportMenu
              {...{ productLineFilter, skuFilter }}
              isDisabled={!numProductLines}
            />

            <DropDownMenu
              buttonAs={CreateButton}
              isDisabled={archivedStatus === ArchivedStatus.Archived}
              isMenuInPortal
            >
              <MenuItem onClick={() => handleCreateNew(["ProductLine"])}>
                Add Product Line…
              </MenuItem>

              <MenuItem
                onClick={() => handleCreateNew(["Sku"])}
                isDisabled={!numProductLines}
              >
                Add SKU…
              </MenuItem>

              <NextLink href={PRODUCTS_IMPORT} passHref legacyBehavior>
                <MenuItem as="a">Import…</MenuItem>
              </NextLink>
            </DropDownMenu>
          </Flex>
        </Flex>

        <FilterBarWithSearch
          key={String(isTableReady)}
          filterDefinitions={filterDefinitions}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          FilterBarContainerComponent={FreestandingFilterBarContainer}
          FiltersContainerComponent={FreestandingFilterWell}
        />
      </Box>

      <Box flex="1 1 100%" minH={0}>
        <ProductsTable
          {...{ data, isLoading, selectedRow }}
          onCreateNew={handleCreateNew}
          onRowSelected={handleRowSelected}
          isCreateDisabled={archivedStatus === ArchivedStatus.Archived}
          placeholderText={
            archivedStatus === ArchivedStatus.Archived
              ? "No archived SKUs found."
              : `No ${
                  archivedStatus === ArchivedStatus.Active ? "active " : ""
                }product lines have been added to your account.`
          }
        />
      </Box>

      <Drawer
        isOpen={!!selectedRow || !!createNew}
        placement="right"
        onClose={handleClose}
        size="sm"
        blockScrollOnMount={false}
        isFullHeight={false}
      >
        <DrawerContent
          {...CONTENT_FULL_HEIGHT_PROPS}
          borderLeftWidth={1}
          zIndex={99}
          backgroundColor="cardBackground"
        >
          {selectedRow?.[0] === "ProductLine" && (
            <ProductLinePanel
              productLineId={selectedRow[1]}
              onArchiveSuccess={handleArchiveSuccess}
              onClose={handleClose}
              onDeleteSuccess={() => {
                handleClose();
              }}
            />
          )}

          {createNew?.[0] === "ProductLine" && (
            <ProductLineCreate
              onClose={handleClose}
              onNewCancel={handleClose}
              onNewSuccess={(createdData) => {
                const { productLine } =
                  createdData.setProductCategoryDepartmentSubDepartmentsForProductLine ||
                  {};
                assertPresence(productLine?.id);
                handleRowSelected("ProductLine", productLine.id);
              }}
            />
          )}

          {selectedRow?.[0] === "Sku" && (
            <SkuPanel
              skuId={selectedRow[1]}
              onArchiveSuccess={handleArchiveSuccess}
              onClose={handleClose}
              onDeleteSuccess={() => {
                handleClose();
              }}
              onDuplicateSku={(skuData) => handleCreateNew(["Sku", skuData])}
            />
          )}

          {createNew?.[0] === "Sku" && (
            <SkuPanelCreate
              initialData={createNew[1]}
              onClose={handleClose}
              onNewCancel={handleClose}
              onNewSuccess={(createdData) => {
                const { sku } = createdData.createSku || {};
                assertPresence(sku);
                handleRowSelected("Sku", sku.id);
              }}
            />
          )}
        </DrawerContent>
      </Drawer>
    </ScrollContainer>
  );
}

ProductsIndex.getLayout = (page: React.ReactElement) => (
  <Layout isFullHeight>{page}</Layout>
);
